import { useEffect, useState } from "react";
import { diffLines } from "diff";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useEditor } from "@/components/editor/useEditorState";

const json = (x) => (x == null ? "" : JSON.stringify(x, null, 2));
const CONTEXT = 3;

function DiffView({ a = "", b = "", testid }) {
  const parts = diffLines(a || "", b || "");
  const rows = [];
  parts.forEach((p, pi) => {
    const lines = p.value.replace(/\n$/, "").split("\n");
    if (!p.added && !p.removed && lines.length > CONTEXT * 2 + 1) {
      const head = pi === 0 ? [] : lines.slice(0, CONTEXT), tail = pi === parts.length - 1 ? [] : lines.slice(-CONTEXT);
      head.forEach((l) => rows.push([" ", l]));
      rows.push(["…", `${lines.length - head.length - tail.length} righe invariate`]);
      tail.forEach((l) => rows.push([" ", l]));
    } else lines.forEach((l) => rows.push([p.added ? "+" : p.removed ? "-" : " ", l]));
  });
  const changed = parts.some((p) => p.added || p.removed);
  if (!changed) return <p className="text-[11px] text-slate-500">Nessuna differenza.</p>;
  return (
    <pre className="max-h-80 overflow-auto rounded-md border bg-slate-950 p-2 text-[11px] leading-[1.45] font-mono" data-testid={testid}>
      {rows.map(([k, l], i) => (
        <div key={i} className={k === "+" ? "bg-emerald-900/50 text-emerald-200" : k === "-" ? "bg-red-900/50 text-red-200" : k === "…" ? "text-sky-300 italic" : "text-slate-400"}>
          <span className="select-none inline-block w-4">{k === "…" ? "" : k}</span>{l}
        </div>
      ))}
    </pre>
  );
}

function OpDiff({ op }) {
  const prev = op.prev || {};
  if (op.text_before != null) {
    return (
      <div className="grid gap-2 sm:grid-cols-2">
        <div className="rounded-md border border-red-200 bg-red-50 p-2 text-sm text-red-900" data-testid={`ai-diff-text-before-${op.index}`}><p className="text-[10px] uppercase tracking-wider text-red-700">Prima</p>{op.text_before}</div>
        <div className="rounded-md border border-emerald-200 bg-emerald-50 p-2 text-sm text-emerald-900" data-testid={`ai-diff-text-after-${op.index}`}><p className="text-[10px] uppercase tracking-wider text-emerald-700">Dopo</p>{op.text_after}</div>
      </div>
    );
  }
  const blocks = [];
  if (op.op === "delete_section") blocks.push(["Sezione eliminata", prev.code || prev.html, ""]);
  if (op.code != null) blocks.push(["Codice", op.op === "add_section" ? "" : prev.code, op.code]);
  if (op.props != null) blocks.push(["Proprietà (testi e immagini)", json(prev.props), json(op.props)]);
  ["html", "css", "js"].forEach((k) => { if (op[k] != null && op.op !== "update_css") blocks.push([k.toUpperCase(), prev[k], op[k]]); });
  if (op.op === "update_css") blocks.push(["CSS globale", prev.css, op.css]);
  if (op.json != null) blocks.push([op.op === "update_data" ? "Dati simulati (DATA)" : "Design", json(prev.json), json(op.json)]);
  if (op.op === "update_page") blocks.push(["Pagina", json(prev), json({ name: op.name, slug: op.slug, in_nav: op.in_nav })]);
  return <div className="space-y-2">{blocks.map(([t, a, b]) => <div key={t}><p className="mb-1 text-[11px] font-medium text-slate-600">{t}</p><DiffView a={a} b={b} testid={`ai-diff-code-${op.index}`} /></div>)}</div>;
}

export default function DiffDialog({ edit, open, onOpenChange }) {
  const { api, id } = useEditor();
  const [detail, setDetail] = useState(null);
  useEffect(() => { if (open) { setDetail(null); api.get(`/projects/${id}/ai-edits/${edit.id}`).then(({ data }) => setDetail(data)); } }, [open, edit.id, api, id]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="ai-diff-dialog">
        <DialogHeader>
          <DialogTitle className="pr-8">Differenze · {edit.label}</DialogTitle>
          <DialogDescription>In rosso cosa viene rimosso, in verde cosa viene aggiunto. Il resto del sito non cambia.</DialogDescription>
        </DialogHeader>
        {!detail ? <Loader2 className="mx-auto animate-spin text-sky-600" /> : (
          <div className="space-y-5">
            {detail.ops.map((op) => (
              <section key={op.index} data-testid={`ai-diff-op-${op.index}`}>
                <p className="text-sm font-semibold text-slate-900">{op.label}{op.error && <span className="ml-2 text-[11px] font-normal text-red-600">Non applicabile: {op.error}</span>}</p>
                <div className="mt-2"><OpDiff op={op} /></div>
              </section>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
